import { Box, Stack, Typography } from "@mui/material";
import type { PaymentStep } from "Components/AnonymousCustomer/Payment/config";
import { InitiatePaymentButton } from "Components/AnonymousCustomer/Payment/PaymentDetail/InitiatePaymentButton";
import { Colors } from "Components/Layout/Themes/Colors";
import { DoneIcon } from "Components/Shared/Icons";
import {
  type PaymentErrorCode,
  TransactionStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { PaymentFailureMessage } from "Components/Transactions/PaymentFailureMessage";

const RESOURCES = Resources.Transactions.TransactionStatusDescription;
const ERROR_RESOURCES = Resources.TransactionErrors;

export const STATUSES_ALLOWING_PAYMENT = [
  TransactionStatus.Created,
  TransactionStatus.Initiated,
  TransactionStatus.Fail,
];

type Props = {
  transactionStatus: TransactionStatus;
  onNextStepRequested: (nextStep?: PaymentStep) => void;
  transactionPublicID: string;
  campaignCode?: string;
  affiliateCode?: string;
  failureReason: PaymentErrorCode;
  pendingTipAmount?: number;
  isExpired?: boolean;
};

export const PaymentStatus = ({
  transactionStatus,
  onNextStepRequested,
  transactionPublicID,
  campaignCode,
  affiliateCode,
  failureReason,
  pendingTipAmount,
  isExpired = false,
}: Props) => {
  const { t } = useResource();

  const isSuccess = transactionStatus === TransactionStatus.Success;
  const isFailed = transactionStatus === TransactionStatus.Fail;

  // Expired payment cannot be started anymore — show the message instead of the pay button,
  // including expired transactions the cancellation job already switched to Cancelled.
  // Processing/Success/InvestigationNeeded keep their own status: the payment was signed
  // before the expiration hit and may still complete, so "expired" would be misleading.
  const isExpiredMessageVisible =
    isExpired &&
    (STATUSES_ALLOWING_PAYMENT.includes(transactionStatus) ||
      transactionStatus === TransactionStatus.Cancelled);

  if (isExpiredMessageVisible) {
    return (
      <Box textAlign="center">
        <Typography variant="h6">
          {t(ERROR_RESOURCES.TransactionExpired)}
        </Typography>
      </Box>
    );
  }

  if (STATUSES_ALLOWING_PAYMENT.includes(transactionStatus)) {
    return (
      <Stack gap={2}>
        {isFailed && <PaymentFailureMessage failureReason={failureReason} />}
        <InitiatePaymentButton
          onNextStepRequested={onNextStepRequested}
          transactionPublicID={transactionPublicID}
          campaignCode={campaignCode}
          affiliateCode={affiliateCode}
          pendingTipAmount={pendingTipAmount}
        />
      </Stack>
    );
  }

  return (
    <Box textAlign="center">
      {isSuccess && <DoneIcon color={Colors.Green} />}
      <Typography variant="h6">{t(RESOURCES[transactionStatus])} </Typography>
    </Box>
  );
};
